import React, { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { api } from "../api";
import { showError } from "../utils/toast";

import "../sass/components/relatorioModal.scss";

/**
 * Modal exclusivo do Relatório de Agendamentos
 * Props:
 *  - open: boolean
 *  - onClose: () => void
 *  - tecnicos: array de usuários (com codFunc e nome/RazaoSocial/Nome)
 *  - inicioDefault?: "YYYY-MM-DD"
 *  - fimDefault?: "YYYY-MM-DD"
 *  - tecnicoDefault?: string (ou "TODOS")
 */
export default function RelatorioAgendamentosModal({
  open,
  onClose,
  tecnicos = [],
  inicioDefault,
  fimDefault,
  tecnicoDefault = "TODOS",
}) {
  const hoje = useMemo(() => new Date().toISOString().slice(0, 10), []);
  const [loading, setLoading] = useState(false);
  const [params, setParams] = useState({
    inicio: inicioDefault || hoje,
    fim: fimDefault || hoje,
    tecnico: tecnicoDefault || "TODOS",
  });
  const [rows, setRows] = useState([]);
  const [selected, setSelected] = useState(null); // linha selecionada p/ leitura

  // helpers de formatação/local
  const joinNonEmpty = (list, sep = " ") =>
    (list || []).map((x) => (x || "").toString().trim()).filter(Boolean).join(sep);

  const montarEndereco = (r = {}) => {
    if (r.EnderecoCompleto) return r.EnderecoCompleto;
    const localEend = joinNonEmpty([r.LocalLogradouro, r.Endereco], " ");
    let txt = localEend || "";
    if (r.Numero) txt += `${txt ? ", " : ""}${r.Numero}`;
    if (r.Bairro) txt += `${txt ? " - " : ""}${r.Bairro}`;
    if (r.Cidade) txt += `${txt ? " - " : ""}${r.Cidade}`;
    if (r.UF) txt += `${txt ? " - " : ""}${r.UF}`;
    return txt;
  };

  const telefonesStr = (r = {}) =>
    joinNonEmpty([r.Tel1, r.Tel2, r.Cel].map((x) => (x || "").trim()), " / ");

  // Sincroniza defaults ao abrir
  useEffect(() => {
    if (!open) return;
    setParams({
      inicio: inicioDefault || hoje,
      fim: fimDefault || hoje,
      tecnico: tecnicoDefault || "TODOS",
    });
    setRows([]);
    setSelected(null);
  }, [open, inicioDefault, fimDefault, tecnicoDefault, hoje]);

  // ESC para fechar
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => e.key === "Escape" && onClose?.();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  // Trava scroll do body
  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => (document.body.style.overflow = prev);
  }, [open]);

  const fetchReport = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/agendamentos", {
        params: {
          inicio: params.inicio,
          fim: params.fim,
          tecnico: params.tecnico || "TODOS",
        },
      });
      setRows(Array.isArray(data) ? data : []);
      setSelected(null); // limpa seleção ao pesquisar
    } catch {
      showError("Falha ao consultar o relatório.");
      setRows([]);
      setSelected(null);
    } finally {
      setLoading(false);
    }
  };

  const exportCSV = () => {
    if (!rows.length) return;
    const headers = [
      "ID","Tecnico","DataISO","HrInicio","TempoExec","Cliente",
      "DescrServ","PedidoCliente","Cidade","Bairro","Pendente",
      "ContatoConfirmacao","Online",
    ];
    const q = (v) => `"${String(v ?? "").replace(/"/g, '""').replace(/\r?\n/g, " ")}"`;
    const body = rows
      .map((r) =>
        [
          r.id, r.Tecnico, r.DataISO, r.HrInicio, r.TempoExec, r.Cliente,
          r.DescrServ, r.PedidoCliente, r.Cidade, r.Bairro, r.Pendente,
          r.ContatoConfirmacao, r.Online,
        ].map(q).join(";")
      )
      .join("\r\n");
    const csv = [headers.join(";"), body].filter(Boolean).join("\r\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `relatorio-agendamentos_${params.inicio}_a_${params.fim}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  if (!open) return null;

  return createPortal(
    <div className="ram-backdrop" role="dialog" aria-modal="true" onClick={onClose}>
      <div className="ram-modal" onClick={(e) => e.stopPropagation()}>
        <header className="ram-head">
          <div className="ram-title">Relatório de Agendamentos (período)</div>
          <button className="icon-btn" aria-label="Fechar" onClick={onClose}>✕</button>
        </header>

        <div className="ram-body">
          <form
            className="ram-filters"
            onSubmit={(e) => {
              e.preventDefault();
              fetchReport();
            }}
          >
            <div className="field">
              <label>Início</label>
              <input
                type="date"
                value={params.inicio}
                onChange={(e) => setParams((p) => ({ ...p, inicio: e.target.value }))}
                autoFocus
              />
            </div>
            <div className="field">
              <label>Fim</label>
              <input
                type="date"
                value={params.fim}
                onChange={(e) => setParams((p) => ({ ...p, fim: e.target.value }))}
              />
            </div>
            <div className="field grow">
              <label>Técnico</label>
              <select
                value={params.tecnico}
                onChange={(e) => setParams((p) => ({ ...p, tecnico: e.target.value }))}
              >
                <option value="TODOS">TODOS</option>
                {tecnicos.map((u) => {
                  const nome = u.nome || u.RazaoSocial || u.Nome;
                  return (
                    <option key={u.codFunc} value={nome}>
                      {nome}
                    </option>
                  );
                })}
              </select>
            </div>
            <div className="field">
              <label>&nbsp;</label>
              <button type="submit" className="btn btn-primary" disabled={loading}>
                {loading ? "Consultando..." : "Pesquisar"}
              </button>
            </div>
          </form>

          {/* Tabela de resultados */}
          <div className="ram-table-wrap ram-mt">
            <table className="table">
              <thead>
                <tr>
                  <th>Data</th>
                  <th>Hr.Início</th>
                  <th>Técnico</th>
                  <th>Cliente</th>
                  <th>Serviço</th>
                  <th>Cidade</th>
                  <th>Bairro</th>
                  <th>Status</th>
                  <th>Tipo</th>
                </tr>
              </thead>
              <tbody>
                {(!rows || rows.length === 0) && (
                  <tr>
                    <td colSpan={9} className="muted">Sem dados.</td>
                  </tr>
                )}
                {rows.map((r) => {
                  const isSel = selected?.id === r.id;
                  return (
                    <tr
                      key={r.id}
                      onClick={() => setSelected(r)}
                      style={{
                        cursor: "pointer",
                        background: isSel ? "rgba(37,99,235,.08)" : undefined,
                      }}
                      title="Clique para ler completo"
                    >
                      <td>{r.DataISO}</td>
                      <td>{r.HrInicio}</td>
                      <td>{r.Tecnico}</td>
                      <td>{r.Cliente}</td>
                      <td>{r.DescrServ}</td>
                      <td>{r.Cidade}</td>
                      <td>{r.Bairro}</td>
                      <td>{r.Pendente === "Confirmado" ? "Confirmado" : "Pendente"}</td>
                      <td>{r.Online}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Painel de leitura (campos somente leitura) */}
          {selected && (
            <div
              className="ram-mt"
              style={{
                border: "1px solid var(--border, #e5e7eb)",
                borderRadius: 10,
                padding: 12,
                background: "var(--surface, #fff)",
              }}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  marginBottom: 8,
                  gap: 8,
                }}
              >
                <strong style={{ fontSize: "0.98rem" }}>
                  Leitura do registro selecionado
                </strong>
                <button
                  type="button"
                  className="btn btn-ghost"
                  onClick={() => setSelected(null)}
                  style={{ padding: "6px 10px" }}
                >
                  Limpar seleção
                </button>
              </div>

              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "1.2fr 1.2fr 1fr 1fr",
                  gap: 12,
                }}
              >
                <div className="field">
                  <label>ID</label>
                  <input type="text" readOnly value={selected.id ?? ""} />
                </div>
                <div className="field">
                  <label>Data</label>
                  <input type="text" readOnly value={selected.DataISO ?? ""} />
                </div>
                <div className="field">
                  <label>Hr.Início</label>
                  <input type="text" readOnly value={selected.HrInicio ?? ""} />
                </div>
                <div className="field">
                  <label>T.Exec</label>
                  <input type="text" readOnly value={selected.TempoExec ?? ""} />
                </div>

                <div className="field" style={{ gridColumn: "span 2" }}>
                  <label>Técnico</label>
                  <input type="text" readOnly value={selected.Tecnico ?? ""} />
                </div>
                <div className="field" style={{ gridColumn: "span 2" }}>
                  <label>Cliente</label>
                  <input type="text" readOnly value={selected.Cliente ?? ""} />
                </div>

                <div className="field" style={{ gridColumn: "span 4" }}>
                  <label>Endereço</label>
                  <input type="text" readOnly value={montarEndereco(selected)} />
                </div>

                <div className="field">
                  <label>Cidade</label>
                  <input type="text" readOnly value={selected.Cidade ?? ""} />
                </div>
                <div className="field">
                  <label>Bairro</label>
                  <input type="text" readOnly value={selected.Bairro ?? ""} />
                </div>
                <div className="field">
                  <label>Status</label>
                  <input
                    type="text"
                    readOnly
                    value={selected.Pendente === "Confirmado" ? "Confirmado" : "Pendente"}
                  />
                </div>
                <div className="field">
                  <label>Tipo</label>
                  <input type="text" readOnly value={selected.Online ?? ""} />
                </div>

                <div className="field">
                  <label>Telefones</label>
                  <input type="text" readOnly value={telefonesStr(selected)} />
                </div>
                <div className="field">
                  <label>Cód. Cliente</label>
                  <input type="text" readOnly value={selected.ChvCad ?? ""} />
                </div>
                <div className="field">
                  <label>Confirmação</label>
                  <input
                    type="text"
                    readOnly
                    value={
                      selected.Pendente === "Confirmado"
                        ? (selected.ContatoConfirmacao || "Confirmado")
                        : "Pendente"
                    }
                  />
                </div>
                <div className="field">
                  <label>Nº Liberação</label>
                  <input type="text" readOnly value={selected.NumeroLiberacao ?? ""} />
                </div>

                <div className="field" style={{ gridColumn: "span 4" }}>
                  <label>Serviço a executar</label>
                  <textarea rows={3} readOnly value={selected.DescrServ ?? ""} />
                </div>

                <div className="field" style={{ gridColumn: "span 4" }}>
                  <label>Pedido do cliente</label>
                  <textarea rows={3} readOnly value={selected.PedidoCliente ?? ""} />
                </div>

                {"Anotacoes" in (selected || {}) && (
                  <div className="field" style={{ gridColumn: "span 4" }}>
                    <label>Anotações</label>
                    <textarea rows={3} readOnly value={selected.Anotacoes ?? ""} />
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        <footer className="ram-foot">
          <div className="muted">
            {rows.length ? `${rows.length} registro(s) encontrado(s)` : ""}
          </div>
          <div className="actions">
            <button className="btn btn-outline" onClick={onClose}>Fechar</button>
            <button className="btn" onClick={exportCSV} disabled={!rows.length}>
              Exportar CSV
            </button>
          </div>
        </footer>
      </div>
    </div>,
    document.body
  );
}
